import { Card, H4 } from "@blueprintjs/core";
import { IN_QUEUE_PATH } from "infrastructure/RouterApp";
import { segmentTrackClickScenarioFromSensitivityChart } from "in_queue/analytics";
import { useCluster } from "in_queue/contexts/ClusterContext";
import { useAllQueueProjects } from "in_queue/contexts/ProjectDataContext";
import { TrackedProject } from "in_queue/types/projectType";
import { ScenarioMetricsMapping } from "in_queue/types/scenarioMetricsType";
import { ScenarioMetadata } from "in_queue/types/scenarioType";
import { findLatestBaseScenario } from "in_queue/utils/baseScenario";
import {
    buildClusterMwMap,
    computeScenarioDropoff,
    formatOwnDownsize
} from "in_queue/utils/dropoffStats";
import { formatPercent } from "in_queue/utils/formatting";
import { getCostAssumptionText } from "in_queue/utils/scenarios";
import { useNavigate } from "react-router-dom";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    LabelList,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";
import { getScenarioTitle, ScenarioAxisTick } from "./chartHelpers";
import {
    ChartMetric,
    chartMetricValue,
    CHART_METRIC_AXIS_LABELS,
    formatChartMetric,
    formatChartMetricDelta
} from "./chartMetric";
import css from "./charts.module.scss";

interface Props {
    /** Completed scenarios with cost metrics already attached. */
    completed: ScenarioMetadata[];
    /** All scenarios for this project — used to find the SHARED base. */
    allScenarios: ScenarioMetadata[];
    project: TrackedProject;
    scenarioMetrics: ScenarioMetricsMapping;
    metric: ChartMetric;
}

/**
 * Scenario sensitivity bar chart: the selected metric on Y, scenarios on
 * X. The base scenario gets a labeled blue bar; non-base bars are colored
 * by Δ vs base (green = cheaper, orange = more expensive). Bars are
 * sorted left-to-right cheapest-first so the chart visually flows
 * green → blue → orange.
 */
export const SensitivityChart: React.FC<Props> = ({
    completed,
    allScenarios,
    project,
    scenarioMetrics,
    metric
}) => {
    const cluster = useCluster();
    const navigate = useNavigate();
    const openScenario = (scenarioId: string) => {
        const url = `/${IN_QUEUE_PATH}/${cluster.slug}/${project.projectId}/scenario/${scenarioId}`;
        navigate(url);
    };

    const valueOf = (sid: string): number | undefined =>
        chartMetricValue(scenarioMetrics[sid], metric);
    const yFormatter = (v: number) => formatChartMetric(metric, v);
    const yLabel = CHART_METRIC_AXIS_LABELS[metric];

    // Cluster MW map — used to compute the dropoff stats shown in the
    // tooltip. Identical to DropoffChart, just surfaced inline here.
    const allQueueProjectsL = useAllQueueProjects();
    const allQueueProjects =
        allQueueProjectsL === "loading" ? [] : allQueueProjectsL;
    const mwMap = buildClusterMwMap(allQueueProjects);

    const withCpm = completed.filter(
        (s) => valueOf(s.scenarioId) !== undefined
    );
    // The outer ProjectCharts gate ensures completed.length >= 2; this is
    // a defensive no-op in case costPerMw data lags behind sumAllocatedCost.
    if (withCpm.length < 2) {
        return null;
    }

    // Fall back to the cheapest scenario in view if no base scenario has
    // results yet (e.g. very early in a study).
    const base = findLatestBaseScenario(
        allScenarios,
        (s) => valueOf(s.scenarioId) !== undefined
    );

    let baseScenarioId: string;
    if (base) {
        baseScenarioId = base.scenarioId;
    } else {
        const fallback = withCpm.reduce((best, cur, i) => {
            const bestVal = valueOf(withCpm[best].scenarioId) ?? Infinity;
            const curVal = valueOf(cur.scenarioId) ?? Infinity;
            return curVal < bestVal ? i : best;
        }, 0);
        baseScenarioId = withCpm[fallback].scenarioId;
    }
    const baseValue = valueOf(baseScenarioId) ?? 0;

    // Sort scenarios on the X axis by cost ascending so the bars read
    // cheapest -> most expensive. Combined with the color coding (green =
    // cheaper than base, blue = base, orange = more expensive) the chart
    // visually flows green -> blue -> orange left to right.
    const data = withCpm
        .map((s) => {
            const value = valueOf(s.scenarioId) ?? 0;
            const dropoff = computeScenarioDropoff(s, project.projectId, mwMap);
            return {
                scenarioId: s.scenarioId,
                label: getScenarioTitle(s),
                value,
                delta: value - baseValue,
                isBase: s.scenarioId === baseScenarioId,
                ownDownsizePct: dropoff.ownDownsizePct,
                ownNrisDownsizePct: dropoff.ownNrisDownsizePct,
                otherProjectCount: dropoff.otherProjectCount,
                otherDropoffMw: dropoff.otherDropoffMw,
                otherDropoffPct: dropoff.otherDropoffPct,
                costAssumption: getCostAssumptionText(s)
            };
        })
        .sort((a, b) => a.value - b.value);

    return (
        <Card className={css.chartCard}>
            <div className={css.chartHeader}>
                <H4 className={css.chartTitle}>Scenario sensitivity</H4>
            </div>
            <ResponsiveContainer width="100%" height={300}>
                <BarChart
                    data={data}
                    margin={{ top: 24, right: 20, bottom: 16, left: 10 }}
                    onClick={(state) => {
                        // Navigate when the user clicks anywhere in a
                        // bar's column area — not just on the bar
                        // itself. Recharts v3 surfaces the active tick
                        // index here (as a stringified index, see
                        // recharts' `combineActiveTooltipIndex`), so a
                        // $0 / zero-height bar still fires as long as
                        // the column was the active tooltip target.
                        const rawIdx = state?.activeTooltipIndex;
                        if (rawIdx == null) return;
                        const idx =
                            typeof rawIdx === "number"
                                ? rawIdx
                                : Number(rawIdx);
                        if (!Number.isFinite(idx)) return;
                        const d = data[idx];
                        if (d?.scenarioId) {
                            segmentTrackClickScenarioFromSensitivityChart({
                                scenarioId: d.scenarioId,
                                position: idx
                            });
                            openScenario(d.scenarioId);
                        }
                    }}
                    style={{ cursor: "pointer" }}
                >
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis
                        dataKey="label"
                        interval={0}
                        tick={ScenarioAxisTick as unknown as undefined}
                        height={80}
                        label={{
                            value: "Scenario",
                            position: "insideBottom",
                            offset: -8,
                            style: { fontSize: 11 }
                        }}
                    />
                    <YAxis
                        // Wide enough for the tick text plus the rotated
                        // label: `position: left` places the label inside
                        // this band, not in the chart margin.
                        width={100}
                        tick={{ fontSize: 11 }}
                        tickFormatter={yFormatter}
                        label={{
                            value: yLabel,
                            angle: -90,
                            position: "left",
                            offset: 0,
                            style: { fontSize: 11, textAnchor: "middle" }
                        }}
                    />
                    <ReferenceLine
                        y={baseValue}
                        stroke="#666"
                        strokeDasharray="4 4"
                    />
                    <Tooltip
                        isAnimationActive={false}
                        content={({ payload }) => {
                            if (!payload?.length) return null;
                            const p = payload[0]
                                .payload as (typeof data)[number];
                            return (
                                <div className={css.tooltip}>
                                    <div>
                                        <b>{p.label}</b>
                                        {p.isBase && " (base)"}
                                    </div>
                                    <div>{yFormatter(p.value)}</div>
                                    {!p.isBase && (
                                        <div>
                                            Δ vs base:{" "}
                                            {formatChartMetricDelta(
                                                metric,
                                                p.delta
                                            )}
                                        </div>
                                    )}
                                    <div className={css.tooltipDivider} />
                                    <div className={css.tooltipSubtle}>
                                        Sensitivities
                                    </div>
                                    <div>
                                        Own downsize:{" "}
                                        {formatOwnDownsize(
                                            p.ownDownsizePct,
                                            p.ownNrisDownsizePct
                                        )}
                                    </div>
                                    <div>
                                        Other projects dropped:{" "}
                                        {p.otherProjectCount === 0
                                            ? "none"
                                            : `${p.otherProjectCount} project${
                                                  p.otherProjectCount === 1
                                                      ? ""
                                                      : "s"
                                              } · ${p.otherDropoffMw.toFixed(
                                                  0
                                              )} MW (${formatPercent(
                                                  p.otherDropoffPct
                                              )} of cluster)`}
                                    </div>
                                    <div>
                                        Cost assumption: {p.costAssumption}
                                    </div>
                                </div>
                            );
                        }}
                    />
                    <Bar dataKey="value" isAnimationActive={false}>
                        {data.map((d, i) => (
                            <Cell
                                key={i}
                                fill={
                                    d.isBase
                                        ? "#2fb0ff"
                                        : d.delta >= 0
                                        ? "#f7a23b"
                                        : "#19c39a"
                                }
                            />
                        ))}
                        {/* "Base Scenario" label above the base bar. */}
                        <LabelList
                            dataKey="isBase"
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            content={(props: any) => {
                                const { x, y, width, value } = props as {
                                    x: number;
                                    y: number;
                                    width: number;
                                    value: boolean;
                                };
                                if (!value) return null;
                                return (
                                    <text
                                        x={x + width / 2}
                                        y={y - 6}
                                        textAnchor="middle"
                                        fontSize={11}
                                        fontWeight={700}
                                        fill="#2fb0ff"
                                    >
                                        Base Scenario
                                    </text>
                                );
                            }}
                        />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </Card>
    );
};
